import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getHdfcRetailUPRecords,
  deleteHdfcRetailUPRecord,
  bulkDeleteHdfcRetailUPRecords,
  importHdfcRetailUPRecords,
  exportHdfcRetailUPRecords,
  updateHdfcRetailUPRecord,
  syncHdfcRetailUPTaskStatus,
  getFieldExecutivesUP,
  getHdfcRetailUPFOS,
  getHdfcRetailUPDepartments,
} from "../actions/hdfcRetailUPActions";
import Button from "../components/Button";
import Loading from "../components/Loading";
import Pagination from "../components/Pagination";
import Table from "../components/Table";
import DriveFilters, {
  driveFilterSelectClass,
} from "../components/DriveFilters";
import {
  Edit,
  Trash2,
  Upload,
  Download,
  Check,
  X,
  Trash,
  Eye,
  Copy,
} from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

// Helper to get last 7 IST dates in YYYY-MM-DD including today
function getLast7HdfcRetailUPDates() {
  const dates = [];
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const istOffset = 5.5 * 60 * 60 * 1000;
  for (let i = 0; i < 7; i++) {
    const istTime = new Date(utc + istOffset - i * 24 * 60 * 60 * 1000);
    const yyyy = istTime.getFullYear();
    const mm = String(istTime.getMonth() + 1).padStart(2, "0");
    const dd = String(istTime.getDate()).padStart(2, "0");
    dates.push(`${yyyy}-${mm}-${dd}`);
  }
  return dates;
}

const HdfcRetailUPPage = () => {
  const dispatch = useDispatch();
  const { getHdfcRetailUPRecordsData, loadingGetHdfcRetailUPRecords } =
    useSelector((state) => state.getHdfcRetailUPRecords || {});

  // Extract data and total from the new structure
  const data = getHdfcRetailUPRecordsData?.data || [];
  const total = getHdfcRetailUPRecordsData?.total || 0;
  const loading = loadingGetHdfcRetailUPRecords || false;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [file, setFile] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [savingCell, setSavingCell] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const inputRef = useRef(null);
  const tableRef = useRef(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRemark, setSelectedRemark] = useState("");
  const [isEditingRemark, setIsEditingRemark] = useState(false);
  const [editRemarkValue, setEditRemarkValue] = useState("");
  const [remarkUpdateLoading, setRemarkUpdateLoading] = useState(false);
  const [viewAddressModalOpen, setViewAddressModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [tatEditModalOpen, setTatEditModalOpen] = useState(false);
  const [tatEditValue, setTatEditValue] = useState("");
  const [tatUpdateLoading, setTatUpdateLoading] = useState(false);
  const [importDate, setImportDate] = useState("");
  const [dates, setDates] = useState([]);
  const [taskIdEditModalOpen, setTaskIdEditModalOpen] = useState(false);
  const [taskIdEditValue, setTaskIdEditValue] = useState("");
  const [taskIdUpdateLoading, setTaskIdUpdateLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [taskStatusEditModalOpen, setTaskStatusEditModalOpen] = useState(false);
  const [taskStatusEditValue, setTaskStatusEditValue] = useState("");
  const [taskStatusUpdateLoading, setTaskStatusUpdateLoading] = useState(false);
  const [filterDate, setFilterDate] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const intervalRef = useRef(null);

  const [fosNameEditModalOpen, setFosNameEditModalOpen] = useState(false);
  const [fosNameSearchValue, setFosNameSearchValue] = useState("");
  const [fosNameUpdateLoading, setFosNameUpdateLoading] = useState(false);
  const [fieldExecutives, setFieldExecutives] = useState([]);
  const [fosNameSearchLoading, setFosNameSearchLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { getFieldExecutivesUPData, loadingGetFieldExecutivesUP } = useSelector(
    (state) => state.getFieldExecutivesUP || {},
  );
  const [losLsMailFilter, setLosLsMailFilter] = useState("");
  const [fosFilter, setFosFilter] = useState("");
  const [fosList, setFosList] = useState([]);
  const [loadingFOS, setLoadingFOS] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [hdfcRetailUPViewOption, setHdfcRetailUPViewOption] = useState(true);
  const [hdfcRetailUPAddOption, setHdfcRetailUPAddOption] = useState(true);
  const [hdfcRetailUPUpdateOption, setHdfcRetailUPUpdateOption] =
    useState(true);
  const [hdfcRetailUPDeleteOption, setHdfcRetailUPDeleteOption] =
    useState(true);

  // Export permission logic (like DelhiPage.js)
  let canExport = false;
  try {
    const userInfo = JSON.parse(localStorage.getItem("userInfo"));
    canExport = userInfo && userInfo.role && userInfo.role.usersExport;
  } catch (e) {
    canExport = false;
  }

  const navigate = useNavigate();

  // Add these useEffect hooks after your existing useEffect declarations

  // Check for auth
  const userLogin = useSelector((state) => state.userLogin);
  const { loadingUserInfo, userInfo } = userLogin;

  useEffect(() => {
    // Check if user info exists
    if (!userInfo) {
      navigate("/");
    }
  }, [userInfo, navigate]);

  // Check for role permissions
  const getRoleInfo = useSelector((state) => state.getRoleInfo);
  const { loadingGetRole, getRoleData } = getRoleInfo;

  useEffect(() => {
    if (getRoleData) {
      // Use driveHdfcRetailUp for HDFC Retail UP page access
      setHdfcRetailUPViewOption(getRoleData.driveHdfcRetailUp);
      setHdfcRetailUPAddOption(getRoleData.driveHdfcRetailUp);
      setHdfcRetailUPUpdateOption(getRoleData.driveHdfcRetailUp);
      setHdfcRetailUPDeleteOption(getRoleData.driveHdfcRetailUp);

      if (!getRoleData.driveHdfcRetailUp) {
        toast("Not Authorized", {
          type: "error",
          hideProgressBar: true,
          autoClose: 2000,
        });
        navigate("/");
      }
    }
  }, [userInfo, getRoleData, navigate]);

  const getCurrentISTDisplay = () => {
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istOffset = 5.5 * 60 * 60 * 1000;
    const currentISTTime = new Date(utc + istOffset);

    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    };

    return currentISTTime.toLocaleDateString("en-IN", options);
  };

  const fields = [
    "s_no",
    "task_id",
    "task_status",
    "code",
    "los",
    "app_name",
    "fi_type",
    "loan_type",
    "address",
    "pin_code",
    "date",
    "time",
    "fos_name",
    "zone",
    "los_ls_mail",
    "type",
    "branch",
    "dept",
    "tat",
    "punch_time",
    "punch_date",
    "remark",
    "status",
    "reviewer_name",
    "revised_reason",
  ];
  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "unassigned", label: "Unassigned" },
    { value: "visit pending", label: "Visit Pending" },
    { value: "finalisation pending", label: "Finalisation Pending" },
    { value: "in progress", label: "In Progress" },
    { value: "mapping", label: "Mapping" },
    { value: "not fired", label: "Not Fired" },
    { value: "waived", label: "Waived" },
    { value: "completed", label: "Completed" },
  ];

  const losLsMailOptions = [
    { value: "", label: "All LOS LS Mail" },
    { value: "LOS", label: "LOS" },
    { value: "LS", label: "LS" },
    { value: "MAIL", label: "MAIL" },
    { value: "Lentra", label: "Lentra" },
  ];

  const filterControls = [
    {
      key: "date",
      label: "Date",
      control: (
        <select
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className={driveFilterSelectClass}
        >
          <option value="">Select Date</option>
          {dates.map((date) => (
            <option key={date} value={date}>
              {date}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "status",
      label: "Status",
      control: (
        <select
          value={statusFilter}
          onChange={(e) => handleStatusFilter(e.target.value)}
          className={driveFilterSelectClass}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "losLsMail",
      label: "LOS LS Mail",
      control: (
        <select
          value={losLsMailFilter}
          onChange={(e) => handleLosLsMailFilter(e.target.value)}
          className={driveFilterSelectClass}
        >
          {losLsMailOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "fos",
      label: "FOS",
      control: (
        <select
          value={fosFilter}
          onChange={(e) => handleFosFilter(e.target.value)}
          className={`${driveFilterSelectClass} disabled:bg-gray-50 disabled:cursor-not-allowed`}
          disabled={loadingFOS}
        >
          <option value="">All FOS</option>
          {fosList.map((fos) => (
            <option key={fos} value={fos}>
              {fos}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "department",
      label: "Department",
      control: (
        <select
          value={departmentFilter}
          onChange={(e) => handleDepartmentFilter(e.target.value)}
          className={`${driveFilterSelectClass} disabled:bg-gray-50 disabled:cursor-not-allowed`}
          disabled={loadingDepartments}
        >
          <option value="">All Departments</option>
          {departments.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>
      ),
    },
  ];

  const PrimaryButton = ({
    children,
    onClick,
    disabled,
    className,
    loading,
  }) => (
    <Button
      onClick={onClick}
      disabled={disabled || loading}
      text={children}
      custom={`bg-blue-500 hover:bg-blue-700 ${className}`}
    />
  );

  const SecondaryButton = ({ children, onClick, className, disabled }) => (
    <Button
      onClick={onClick}
      disabled={disabled}
      text={children}
      custom={`bg-gray-500 hover:bg-gray-700 ${className}`}
    />
  );

  const DangerButton = ({ children, onClick, className, loading }) => (
    <Button
      onClick={onClick}
      disabled={loading}
      text={children}
      custom={`bg-red-500 hover:bg-red-700 ${className}`}
    />
  );

  const ModalActionButtons = ({ children, className }) => (
    <div className={`flex gap-2 justify-end ${className}`}>{children}</div>
  );

  const columns = [
    { field: "s_no", header: "S.no." },
    {
      field: "task_id",
      header: "Task ID",
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[60px]">
            {rowData.task_id || "-"}
          </span>
          <button
            onClick={() => {
              setTaskIdEditValue(rowData.task_id || "");
              setFormData(rowData);
              setTaskIdEditModalOpen(true);
            }}
            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="Edit Task ID"
          >
            <Edit className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    {
      field: "task_status",
      header: "Task Status",
      body: (rowData) => {
        const status = rowData.task_status;

        // Define color mapping (same as other pages)
        const getStatusColor = (status) => {
          if (!status) return "bg-gray-100 text-gray-600"; // Default for empty

          const statusLower = status.toLowerCase().trim();

          switch (statusLower) {
            case "unassigned":
              return "bg-red-100 text-red-800 border-red-200";
            case "visit pending":
              return "bg-orange-100 text-orange-800 border-orange-200";
            case "finalisation pending":
              return "bg-yellow-100 text-yellow-800 border-yellow-200";
            case "completed":
              return "bg-green-100 text-green-800 border-green-200";
            default:
              return "bg-gray-100 text-gray-600 border-gray-200";
          }
        };

        return (
          <div className="flex items-center gap-2 min-w-[160px]">
            <span
              className={`px-3 py-2 rounded-full text-sm font-medium border min-w-[120px] text-center ${getStatusColor(status)}`}
              title={status || "No Status"}
            >
              {status || "-"}
            </span>
            {/* <button
                            onClick={() => {
                                setTaskStatusEditValue(rowData.task_status || '');
                                setFormData(rowData);
                                setTaskStatusEditModalOpen(true);
                            }}
                            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
                            title="Edit Task Status"
                        >
                            <Edit className="h-4 w-4" />
                        </button> */}
          </div>
        );
      },
    },
    { field: "import_date", header: "Import Date" },
    { field: "code", header: "Code" },
    { field: "los", header: "LOS" },
    { field: "app_name", header: "App Name" },
    { field: "fi_type", header: "FI Type" },
    { field: "loan_type", header: "Loan Type" },
    {
      field: "address",
      header: "Address",
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[100px]">
            {rowData.address?.length > 20
              ? rowData.address.slice(0, 20) + "..."
              : rowData.address || ""}
          </span>
          <button
            onClick={() => {
              setSelectedAddress(rowData.address || "No address");
              setViewAddressModalOpen(true);
            }}
            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="View address"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    { field: "pin_code", header: "Pin Code" },
    { field: "date", header: "Date" },
    { field: "time", header: "Time" },
    {
      field: "fos_name",
      header: "FOS Name",
      body: (rowData) => {
        // Hide FOS Name if status is "Sent To Bank"
        if (rowData.task_status === "Sent To Bank") {
          return <div className="text-gray-400">-</div>;
        }
        return (
          <div className="flex items-center gap-2">
            <span className="truncate max-w-[100px]">
              {rowData.fos_name || "-"}
            </span>
            <button
              onClick={() => {
                setFosNameSearchValue(rowData.fos_name || "");
                setFormData(rowData);
                setFosNameEditModalOpen(true);
                // Fetch initial field executives
                dispatch(getFieldExecutivesUP(""));
              }}
              className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
              title="Edit FOS Name"
            >
              <Edit className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },

    { field: "zone", header: "Zone" },
    { field: "los_ls_mail", header: "LOS LS Mail" },
    { field: "type", header: "Type" },
    { field: "branch", header: "Branch" },
    { field: "dept", header: "Department" },
    {
      field: "tat",
      header: "TAT",
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[60px]">{rowData.tat || "-"}</span>
          <button
            onClick={() => {
              setTatEditValue(rowData.tat || "");
              setFormData(rowData);
              setTatEditModalOpen(true);
            }}
            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="Edit TAT"
          >
            <Edit className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    { field: "punch_time", header: "Punch Time" },
    { field: "punch_date", header: "Punch Date" },
    {
      field: "remark",
      header: "Remark",
      body: (rowData) => {
        const remark = rowData.remark;
        return (
          <div className="flex items-center gap-2">
            <span className="truncate max-w-[100px]">
              {remark?.length > 20 ? remark.slice(0, 20) + "..." : remark || ""}
            </span>
            <button
              onClick={() => {
                setEditRemarkValue(remark || "");
                setFormData(rowData); // sets _id and other data
                setViewModalOpen(true);
              }}
              className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
              title="Edit remark"
            >
              <Eye className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    { field: "status", header: "Status" },
    { field: "reviewer_name", header: "Reviewer Name" },
    { field: "revised_reason", header: "Revised Reason" },
  ];

  const fetchData = () => {
    let query = `search=${search}&page=${page}&limit=10`;
    if (filterDate) query += `&importDate=${filterDate}`;
    else query += `&importDate=${importDate}`;
    if (statusFilter) query += `&status=${statusFilter}`;
    if (losLsMailFilter) query += `&losLsMailFilter=${losLsMailFilter}`;
    dispatch(getHdfcRetailUPRecords(query));
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Remark copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Failed to copy remark");
    }
  };

  const handleLosLsMailFilter = (selectedLosLsMail) => {
    console.log(
      "HDFC Retail UP LOS LS Mail filter changed to:",
      selectedLosLsMail,
    );
    setLosLsMailFilter(selectedLosLsMail);
    setPage(1);
  };

  const handleFosFilter = (selectedFos) => {
    console.log("HDFC Retail UP FOS filter changed to:", selectedFos);
    setFosFilter(selectedFos);
    setPage(1);
  };

  const handleDepartmentFilter = (selectedDepartment) => {
    console.log(
      "HDFC Retail UP Department filter changed to:",
      selectedDepartment,
    );
    setDepartmentFilter(selectedDepartment);
    setPage(1);
  };

  useEffect(() => {
    // Always show 7-day range in dropdown
    setDates(getLast7HdfcRetailUPDates());

    // By default, set importDate to 7 days range
    const last7Days = getLast7HdfcRetailUPDates();
    const sevenDayRange = last7Days.join(",");

    setImportDate(sevenDayRange);
    setFilterDate("");
    setStatusFilter("");
    setLosLsMailFilter("");
    setFosFilter("");

    // Fetch FOS on mount
    const fetchFOS = async () => {
      try {
        setLoadingFOS(true);
        const result = await dispatch(getHdfcRetailUPFOS(sevenDayRange));
        if (result && result.data) {
          setFosList(result.data);
        } else {
          setFosList([]);
        }
      } catch (error) {
        console.error("Error fetching FOS:", error);
        setFosList([]);
      } finally {
        setLoadingFOS(false);
      }
    };
    fetchFOS();
  }, [dispatch]);

  // Fetch FOS based on current date range (dynamic filter)
  useEffect(() => {
    const fetchFOS = async () => {
      try {
        setLoadingFOS(true);
        // Build query with current date range to get FOS for visible tasks only
        let dateToUse = "";

        // Use filterDate if selected, otherwise use importDate (7-day range)
        if (filterDate) {
          dateToUse = filterDate;
        } else {
          dateToUse = importDate;
        }

        console.log("Fetching HDFC Retail UP FOS with date range:", dateToUse);
        // Pass importDate as query parameter to backend
        const result = await dispatch(getHdfcRetailUPFOS(dateToUse));
        if (result && result.data) {
          setFosList(result.data);
          console.log(
            "HDFC Retail UP FOS list updated for date range:",
            dateToUse,
            "FOS:",
            result.data,
          );
        }
      } catch (error) {
        console.error("Error fetching HDFC Retail UP FOS:", error);
        setFosList([]);
      } finally {
        setLoadingFOS(false);
      }
    };

    fetchFOS();
    // Re-fetch FOS when date range changes
  }, [dispatch, importDate, filterDate]);

  // Fetch Departments based on current date range (dynamic filter)
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);
        let dateToUse = filterDate ? filterDate : importDate;
        console.log(
          "Fetching HDFC Retail UP Departments with date range:",
          dateToUse,
        );
        const result = await dispatch(getHdfcRetailUPDepartments(dateToUse));
        if (result && result.data) {
          setDepartments(result.data);
        }
      } catch (error) {
        console.error("Error fetching HDFC Retail UP Departments:", error);
        setDepartments([]);
      } finally {
        setLoadingDepartments(false);
      }
    };
    fetchDepartments();
  }, [dispatch, importDate, filterDate]);

  useEffect(() => {
    const validPage = Math.max(1, parseInt(page) || 1);
    if (validPage !== page) {
      setPage(validPage);
      return;
    }
    let query = `search=${search}&page=${validPage}&limit=10`;
    if (filterDate) query += `&importDate=${filterDate}`;
    else query += `&importDate=${importDate}`;
    if (statusFilter) query += `&status=${statusFilter}`;
    if (losLsMailFilter) query += `&losLsMailFilter=${losLsMailFilter}`;
    if (fosFilter) query += `&fosFilter=${fosFilter}`;
    if (departmentFilter)
      query += `&dept=${encodeURIComponent(departmentFilter)}`;
    dispatch(getHdfcRetailUPRecords(query));
  }, [
    dispatch,
    search,
    page,
    importDate,
    filterDate,
    statusFilter,
    losLsMailFilter,
    fosFilter,
    departmentFilter,
  ]);

  useEffect(() => {
    console.log("getFieldExecutivesUPData changed:", getFieldExecutivesUPData);
    if (getFieldExecutivesUPData?.data) {
      console.log("Setting fieldExecutives:", getFieldExecutivesUPData.data);
      setFieldExecutives(getFieldExecutivesUPData.data);
    }
  }, [getFieldExecutivesUPData]);

  // Add this callback function
  const handleFosNameSearch = useCallback(
    async (searchTerm) => {
      console.log("handleFosNameSearch called with:", searchTerm);
      setFosNameSearchValue(searchTerm);

      if (searchTerm.length > 0) {
        try {
          setFosNameSearchLoading(true);
          console.log("Dispatching getFieldExecutivesUP...");

          const result = await dispatch(getFieldExecutivesUP(searchTerm));
          console.log("getFieldExecutivesUP result:", result);

          if (result && result.data) {
            setFieldExecutives(result.data);
            setShowSuggestions(true);
          } else {
            setFieldExecutives([]);
            setShowSuggestions(false);
          }
        } catch (error) {
          console.error("Error searching field executives:", error);
          setFieldExecutives([]);
          setShowSuggestions(false);
        } finally {
          setFosNameSearchLoading(false);
        }
      } else {
        setFieldExecutives([]);
        setShowSuggestions(false);
      }
    },
    [dispatch],
  );

  const refreshData = async () => {
    try {
      // Sync task statuses, FOS names, and remarks first
      console.log("Syncing HDFC Retail UP task data before refresh...");
      const syncResult = await dispatch(syncHdfcRetailUPTaskStatus());

      // If sync returned updated records, update local state first
      if (
        syncResult &&
        syncResult.updatedRecords &&
        syncResult.updatedRecords.length > 0
      ) {
        // Note: Since we're not maintaining local state updates like other pages,
        // we'll rely on the full data refresh below
      }

      // Then fetch updated data
      let query = `search=${search}&page=${page}&limit=10`;
      if (filterDate) query += `&importDate=${filterDate}`;
      else query += `&importDate=${importDate}`;
      if (statusFilter) query += `&status=${statusFilter}`;
      if (losLsMailFilter) query += `&losLsMailFilter=${losLsMailFilter}`;
      if (fosFilter) query += `&fosFilter=${fosFilter}`;
      if (departmentFilter)
        query += `&dept=${encodeURIComponent(departmentFilter)}`;

      dispatch(getHdfcRetailUPRecords(query));
    } catch (error) {
      console.error("Error during HDFC Retail UP sync in refreshData:", error);
      // Still fetch data even if sync fails
      let query = `search=${search}&page=${page}&limit=10`;
      if (filterDate) query += `&importDate=${filterDate}`;
      else query += `&importDate=${importDate}`;
      if (statusFilter) query += `&status=${statusFilter}`;
      if (losLsMailFilter) query += `&losLsMailFilter=${losLsMailFilter}`;
      if (fosFilter) query += `&fosFilter=${fosFilter}`;
      if (departmentFilter)
        query += `&dept=${encodeURIComponent(departmentFilter)}`;

      dispatch(getHdfcRetailUPRecords(query));
    }
  };

  // Sync on page load/refresh only (automatic interval sync disabled)
  useEffect(() => {
    const performRealtimeSync = async () => {
      try {
        console.log("Starting HDFC Retail UP realtime sync...");

        // Call the sync endpoint
        const syncResult = await dispatch(syncHdfcRetailUPTaskStatus());

        // If there are updated records, show toast and refresh data
        if (
          syncResult &&
          syncResult.updatedRecords &&
          syncResult.updatedRecords.length > 0
        ) {
          console.log(
            `Found ${syncResult.updatedRecords.length} updated HDFC Retail UP records`,
          );

          // Enhanced toast notification
          const statusUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_status,
          ).length;
          const fosNameUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_fos_name,
          ).length;
          const remarkUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_remark,
          ).length;
          const finalisationStatusUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_finalisation_status,
          ).length;
          const finalisationRemarksUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_finalisation_remarks,
          ).length;

          let message = "";
          if (statusUpdates > 0) message += `${statusUpdates} status`;
          if (fosNameUpdates > 0)
            message += `${message ? ", " : ""}${fosNameUpdates} FOS name`;
          if (remarkUpdates > 0)
            message += `${message ? ", " : ""}${remarkUpdates} remark`;
          if (finalisationStatusUpdates > 0)
            message += `${message ? ", " : ""}${finalisationStatusUpdates} finalisation status`;
          if (finalisationRemarksUpdates > 0)
            message += `${message ? ", " : ""}${finalisationRemarksUpdates} finalisation remarks`;

          // if (message) {
          //     toast.success(`HDFC Retail UP: ${message} updated automatically`, {
          //         position: "bottom-right",
          //         autoClose: 3000,
          //     });
          // }

          // Refresh data to get the latest from server
          let query = `search=${search}&page=${page}&limit=10`;
          if (filterDate) query += `&importDate=${filterDate}`;
          else query += `&importDate=${importDate}`;
          if (statusFilter) query += `&status=${statusFilter}`;
          if (losLsMailFilter) query += `&losLsMailFilter=${losLsMailFilter}`;
          if (fosFilter) query += `&fosFilter=${fosFilter}`;
          if (departmentFilter)
            query += `&dept=${encodeURIComponent(departmentFilter)}`;

          dispatch(getHdfcRetailUPRecords(query));
        }

        setLastRefresh(new Date());
      } catch (error) {
        console.error("HDFC Retail UP realtime sync failed:", error);
        // Fallback to full refresh if realtime sync fails
        refreshData();
      }
    };

    // Sync ONLY on initial page load (not on filter/page changes)
    performRealtimeSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (searchTerm) => {
    setSearch(searchTerm);
    setPage(1); // Reset to first page when searching
  };

  const handleStatusFilter = (selectedStatus) => {
    setStatusFilter(selectedStatus);
    setPage(1);
  };

  const handleTatUpdate = async () => {
    try {
      setTatUpdateLoading(true);

      // Get current IST time when TAT is filled
      const now = new Date();
      const utc = now.getTime() + now.getTimezoneOffset() * 60000;
      const istOffset = 5.5 * 60 * 60 * 1000;
      const currentISTTime = new Date(utc + istOffset);

      // Format punch_date (YYYY-MM-DD for database)
      const yyyy = currentISTTime.getFullYear();
      const mm = String(currentISTTime.getMonth() + 1).padStart(2, "0");
      const dd = String(currentISTTime.getDate()).padStart(2, "0");
      const formattedPunchDate = `${yyyy}-${mm}-${dd}`;

      // Format punch_time (HH:MM AM/PM)
      let hours = currentISTTime.getHours();
      const minutes = currentISTTime.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      const formattedPunchTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm}`;

      // Update with TAT and punch time/date
      await dispatch(
        updateHdfcRetailUPRecord(formData._id, {
          ...formData,
          tat: tatEditValue,
          punch_date: formattedPunchDate,
          punch_time: formattedPunchTime,
          // Automatically set task_status to "Sent To Bank" when TAT is filled
          task_status:
            tatEditValue && tatEditValue.trim() !== ""
              ? "Sent To Bank"
              : formData.task_status,
          manualTaskStatus: true, // Mark as manually modified
        }),
      );

      setTatEditModalOpen(false);
      refreshData();
      toast.success(
        'TAT updated with current punch time and status set to "Sent To Bank"!',
      );
    } catch (err) {
      toast.error("Failed to update TAT");
    } finally {
      setTatUpdateLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error("Please select rows to delete");
      return;
    }
    try {
      setBulkDeleteLoading(true);
      const idsArray = selectedRows.map((row) => row._id);
      await dispatch(bulkDeleteHdfcRetailUPRecords(idsArray));
      setSelectedRows([]);
      setShowBulkDeleteConfirm(false);
      refreshData(); // Changed from fetchData
      toast.success("Bulk delete successful!");
    } catch (err) {
      toast.error("Bulk delete failed!");
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleCellClick = (rowId, field, currentValue) => {
    setEditingCell({ rowId, field });
    setEditValue(currentValue || "");
  };

  const handleCellSave = async (rowId, field, newValue) => {
    if (savingCell) return; // Prevent multiple saves

    setSavingCell({ rowId, field });
    try {
      await dispatch(updateHdfcRetailUPRecord(rowId, { [field]: newValue }));
      // Remove the setData line - Redux will handle state update
      setEditingCell(null);
    } catch (err) {
      console.error("Cell Update Error:", err.message);
      alert("Update Failed: " + (err.response?.data?.message || err.message));
    } finally {
      setSavingCell(null);
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditValue("");
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file to import");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    try {
      setImportLoading(true);
      await dispatch(importHdfcRetailUPRecords(form));
      setModalOpen(false);
      setFile(null);
      refreshData(); // Changed from fetchData
      toast.success("Import Success!");
    } catch (err) {
      toast.error("Import Failed!");
    } finally {
      setImportLoading(false);
    }
  };

  const handleKeyDown = (e, rowId, field) => {
    if (e.key === "Enter") {
      handleCellSave(rowId, field, editValue);
    } else if (e.key === "Escape") {
      handleCellCancel();
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      await dispatch(exportHdfcRetailUPRecords());
      toast.success("Export Success!");
    } catch (err) {
      toast.error("Export Failed!");
    } finally {
      setExportLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteHdfcRetailUPRecord(id));
      refreshData(); // Changed from fetchData
      toast.success("Record deleted successfully!");
    } catch (err) {
      toast.error("Delete Failed!");
    }
  };

  const handleEdit = (row) => {
    setFormData(row);
    setEditModalOpen(true);
  };

  const actions = [
    {
      icon: <Edit className="h-4 w-4" />,
      className:
        "text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50",
      onClick: (row) => handleEdit(row),
    },
    {
      icon: <Trash2 className="h-4 w-4" />,
      className: "text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50",
      onClick: (row) => handleDelete(row._id),
    },
  ];

  const handleUpdate = async () => {
    try {
      setUpdateLoading(true);
      await dispatch(updateHdfcRetailUPRecord(formData._id, formData));
      setEditModalOpen(false);
      refreshData(); // Changed from fetchData
      toast.success("Record updated successfully!");
    } catch (err) {
      toast.error("Update Failed!");
    } finally {
      setUpdateLoading(false);
    }
  };

  const totalPages = Math.ceil(total / 10);
  const allRowsSelected =
    data.length > 0 && selectedRows.length === data.length;
  const someRowsSelected =
    selectedRows.length > 0 && selectedRows.length < data.length;

  const renderCell = (row, field) => {
    const isEditing =
      editingCell?.rowId === row._id && editingCell?.field === field;
    const isSaving =
      savingCell?.rowId === row._id && savingCell?.field === field;
    const cellValue = row[field] || "";

    if (isEditing) {
      return (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, row._id, field)}
            className="w-full px-2 py-1 text-sm border border-indigo-500 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
            style={{ minWidth: "120px" }}
          />
          <div className="absolute right-1 top-1 flex gap-1">
            <button
              onClick={() => handleCellSave(row._id, field, editValue)}
              className="text-green-600 hover:text-green-800 p-0.5"
              title="Save"
              disabled={isSaving}
            >
              <Check className="h-3 w-3" />
            </button>
            <button
              onClick={handleCellCancel}
              className="text-red-600 hover:text-red-800 p-0.5"
              title="Cancel"
              disabled={isSaving}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className={`max-w-xs truncate cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-colors ${
          isSaving ? "bg-yellow-50 opacity-75" : ""
        }`}
        onClick={() => handleCellClick(row._id, field, cellValue)}
        title={cellValue || "Click to edit"}
      >
        {isSaving ? (
          <div className="flex items-center gap-1">
            <div className="animate-spin h-3 w-3 border border-gray-300 border-t-indigo-600 rounded-full"></div>
            <span className="text-xs text-gray-500">Saving...</span>
          </div>
        ) : (
          cellValue || "-"
        )}
      </div>
    );
  };

  // Add formatted import_date to each row
  const formattedData = (data || []).map((row, index) => ({
    ...row,
    s_no: row.s_no ?? (page - 1) * 10 + index + 1,
    import_date: row.createdAt
      ? (() => {
          const dateObj = new Date(row.createdAt);
          if (!isNaN(dateObj.getTime())) {
            const day = String(dateObj.getDate()).padStart(2, "0");
            const month = String(dateObj.getMonth() + 1).padStart(2, "0");
            const year = String(dateObj.getFullYear()).slice(-2);
            return `${day}-${month}-${year}`;
          }
          return "-";
        })()
      : "-",
  }));

  // Helper to get last 3 IST dates in YYYY-MM-DD, excluding today
  function getLast5IstdatesExcludeToday() {
    const dates = [];
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istOffset = 5.5 * 60 * 60 * 1000;
    // Include today (i = 0) and go back 6 more days for total of 7 days
    for (let i = 0; i <= 6; i++) {
      const istTime = new Date(utc + istOffset - i * 24 * 60 * 60 * 1000);
      const yyyy = istTime.getFullYear();
      const mm = String(istTime.getMonth() + 1).padStart(2, "0");
      const dd = String(istTime.getDate()).padStart(2, "0");
      dates.push(`${yyyy}-${mm}-${dd}`);
    }
    return dates;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  HDFC Retail UP Records
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Manage and view HDFC Retail UP loan records • Click any cell
                  to edit inline
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedRows.length > 0 && (
                  <DangerButton
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    className="flex items-center gap-2"
                    disabled={bulkDeleteLoading}
                  >
                    <Trash className="h-4 w-4" />
                    Delete Selected ({selectedRows.length})
                  </DangerButton>
                )}
                <PrimaryButton
                  onClick={() => setModalOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Import
                </PrimaryButton>
                {canExport && (
                  <PrimaryButton
                    onClick={handleExport}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>

          <DriveFilters
            searchValue={search}
            onSearchChange={handleSearch}
            searchPlaceholder="Search by name,los,fos,code,zone,app name..."
            filters={filterControls}
            total={total}
            columnsClass="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
          />
          {selectedRows.length > 0 && (
            <div className="px-6 pb-4">
              <div className="inline-flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-sm text-indigo-700">
                ✓ {selectedRows.length} row
                {selectedRows.length !== 1 ? "s" : ""} selected
              </div>
            </div>
          )}
        </div>

        {/* Table Container */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-hidden">
            {loading ? (
              <div className="py-12">
                <Loading />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto" ref={tableRef}>
                  <Table
                    data={formattedData}
                    columns={columns}
                    loading={loading}
                    pagination={{ totalRecords: total }}
                    currentPage={page}
                    onPageChange={setPage}
                    actions={actions} // Define actions as needed
                    selectable={true}
                    selectedRows={selectedRows}
                    onSelectionChange={setSelectedRows}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 text-center mb-4">
                Delete Selected Records
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to delete {selectedRows.length} selected
                records? This action cannot be undone.
              </p>

              <ModalActionButtons>
                <DangerButton
                  onClick={handleBulkDelete}
                  loading={bulkDeleteLoading}
                  className="flex items-center gap-2"
                >
                  <Trash className="h-4 w-4" />
                  Delete {selectedRows.length} Records
                </DangerButton>
                <SecondaryButton
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  disabled={bulkDeleteLoading}
                >
                  Cancel
                </SecondaryButton>
              </ModalActionButtons>
            </div>
          </div>
        </div>
      )}

      {/* Import/Export Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white m-4">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Import / Export Data
              </h3>
              {/* Template Download Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="text-md font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  Excel Template Format
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                      <h5 className="text-sm font-semibold text-yellow-800 mb-2">
                        Important Notes:
                      </h5>
                      <ul className="text-xs text-yellow-700 space-y-1">
                        <li>• Column names are case-sensitive</li>
                        <li>• Date format: DD/MM/YYYY</li>
                        <li>• Time format: HH:MM AM/PM</li>
                        <li>• TAT should be numeric values</li>
                        <li>• Empty cells will be treated as empty strings</li>
                        <li>• '#N/A' values will be preserved as-is</li>
                        <li>• Zone field defaults to '#N/A' if empty</li>
                      </ul>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <h5 className="text-sm font-semibold text-green-800 mb-2">
                        Download Template:
                      </h5>
                      <p className="text-xs text-green-700 mb-3">
                        Click below to download a sample Excel template with the
                        correct column structure.
                      </p>
                      <button
                        onClick={() => {
                          const link = document.createElement("a");
                          link.href = "/hdfc_retail_up_template.xlsx";
                          link.download = "HDFC_Retail_UP_Import_Template.xlsx";
                          link.click();
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        Download Template
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* File Upload Section */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select file to import
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  />
                </div>
              </div>
              <ModalActionButtons className="mt-6">
                <PrimaryButton
                  onClick={handleImport}
                  disabled={!file}
                  loading={importLoading}
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Import
                </PrimaryButton>
                {canExport && (
                  <PrimaryButton
                    onClick={handleExport}
                    loading={exportLoading}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export
                  </PrimaryButton>
                )}
                <SecondaryButton onClick={() => setModalOpen(false)}>
                  Cancel
                </SecondaryButton>
              </ModalActionButtons>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white m-4">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit HDFC Retail UP Record
              </h3>

              <div className="max-h-96 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {fields.map((field) => (
                    <div key={field}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.replace(/_/g, " ")}
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={formData[field] || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, [field]: e.target.value })
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>

              <ModalActionButtons className="mt-6">
                <PrimaryButton
                  onClick={handleUpdate}
                  loading={updateLoading}
                  className="flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Update
                </PrimaryButton>
                <SecondaryButton onClick={() => setEditModalOpen(false)}>
                  Cancel
                </SecondaryButton>
              </ModalActionButtons>
            </div>
          </div>
        </div>
      )}

      {/* View/Edit Remark Modal */}
      {viewModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Edit Remark
                </h3>

                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={() => copyToClipboard(editRemarkValue)}
                    className="flex items-center gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copy
                  </PrimaryButton>

                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setRemarkUpdateLoading(true);
                        const updated = {
                          ...formData,
                          remark: editRemarkValue,
                        };
                        await dispatch(
                          updateHdfcRetailUPRecord(`${formData._id}`, updated),
                        );
                        setViewModalOpen(false);
                        refreshData();
                        toast.success("Remark updated successfully!");
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to update remark");
                      } finally {
                        setRemarkUpdateLoading(false);
                      }
                    }}
                    loading={remarkUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>

                  <button
                    onClick={() => setViewModalOpen(false)}
                    disabled={remarkUpdateLoading}
                    className="p-2 rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    title="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div
                className="bg-gray-50 p-4 rounded-md border"
                style={{ height: "400px" }}
              >
                <textarea
                  value={editRemarkValue}
                  onChange={(e) => setEditRemarkValue(e.target.value)}
                  className="w-full h-full p-0 border-none bg-transparent text-sm text-gray-700 whitespace-pre-wrap leading-relaxed resize-none focus:outline-none overflow-hidden"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Address View Modal */}
      {viewAddressModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  View Address
                </h3>
                <SecondaryButton onClick={() => setViewAddressModalOpen(false)}>
                  Close
                </SecondaryButton>
              </div>
              <div
                className="bg-gray-50 p-4 rounded-md border"
                style={{ height: "300px" }}
              >
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed overflow-y-auto h-full">
                  {selectedAddress}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAT Edit Modal */}
      {tatEditModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit TAT
                </h3>
                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={handleTatUpdate}
                    loading={tatUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => setTatEditModalOpen(false)}
                    disabled={tatUpdateLoading}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border">
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 mb-3"
                  value={tatEditValue}
                  onChange={(e) => setTatEditValue(e.target.value)}
                  placeholder="Enter TAT "
                />
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <div className="text-sm">
                    <span className="font-medium text-blue-800">
                      Current IST Time:{" "}
                    </span>
                    <span className="text-blue-700">
                      {getCurrentISTDisplay()}
                    </span>
                  </div>
                  <div className="text-xs text-blue-600 mt-1">
                    This time will be captured as punch time when you save
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task ID Edit Modal */}
      {taskIdEditModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit Task ID
                </h3>
                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setTaskIdUpdateLoading(true);
                        const updated = {
                          ...formData,
                          task_id: taskIdEditValue,
                        };
                        await dispatch(
                          updateHdfcRetailUPRecord(formData._id, updated),
                        );
                        setTaskIdEditModalOpen(false);
                        refreshData();
                        toast.success("Task ID updated successfully!");
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to update Task ID");
                      } finally {
                        setTaskIdUpdateLoading(false);
                      }
                    }}
                    loading={taskIdUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => setTaskIdEditModalOpen(false)}
                    disabled={taskIdUpdateLoading}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border">
                <input
                  type="number"
                  value={taskIdEditValue}
                  onChange={(e) => setTaskIdEditValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter Task ID"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Status Edit Modal */}
      {taskStatusEditModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit Task Status
                </h3>
                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setTaskStatusUpdateLoading(true);
                        const updated = {
                          ...formData,
                          task_status: taskStatusEditValue,
                        };
                        await dispatch(
                          updateHdfcRetailUPRecord(formData._id, updated),
                        );
                        setTaskStatusEditModalOpen(false);
                        refreshData();
                        toast.success("Task Status updated successfully!");
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to update Task Status");
                      } finally {
                        setTaskStatusUpdateLoading(false);
                      }
                    }}
                    loading={taskStatusUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => setTaskStatusEditModalOpen(false)}
                    disabled={taskStatusUpdateLoading}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border">
                <input
                  type="text"
                  value={taskStatusEditValue}
                  onChange={(e) => setTaskStatusEditValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter Task Status"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FOS Name Edit Modal */}
      {fosNameEditModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit FOS Name
                </h3>
                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setFosNameUpdateLoading(true);
                        const updated = {
                          ...formData,
                          fos_name: fosNameSearchValue,
                        };
                        await dispatch(
                          updateHdfcRetailUPRecord(formData._id, updated),
                        );
                        setFosNameEditModalOpen(false);
                        setShowSuggestions(false);
                        refreshData();
                        toast.success("FOS Name updated successfully!");
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to update FOS Name");
                      } finally {
                        setFosNameUpdateLoading(false);
                      }
                    }}
                    loading={fosNameUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => {
                      setFosNameEditModalOpen(false);
                      setShowSuggestions(false);
                      setFieldExecutives([]);
                    }}
                    disabled={fosNameUpdateLoading}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-md border relative">
                <div className="relative">
                  <input
                    type="text"
                    value={fosNameSearchValue}
                    onChange={(e) => handleFosNameSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Search Field Executive..."
                    autoComplete="off"
                  />

                  {fosNameSearchLoading && (
                    <div className="absolute right-3 top-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                    </div>
                  )}

                  {/* Suggestions Dropdown */}
                  {showSuggestions && fieldExecutives.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {fieldExecutives.map((executive) => (
                        <div
                          key={executive._id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => {
                            setFosNameSearchValue(executive.name);
                            setShowSuggestions(false);
                          }}
                        >
                          <div className="text-sm font-medium text-gray-900">
                            {executive.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {executive.email} • {executive.identifier}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showSuggestions &&
                    fieldExecutives.length === 0 &&
                    fosNameSearchValue.length > 0 &&
                    !fosNameSearchLoading && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No Field Executives found
                        </div>
                      </div>
                    )}
                </div>

                <div className="mt-3 text-xs text-gray-500">
                  Type to search for Field Executive users
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HdfcRetailUPPage;
